import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { smsConfirmationSchema, type SmsConfirmationFormValues } from '../schemas';

interface SmsConfirmationStepProps {
  onSubmit: (code: string) => Promise<void>;
}

export function SmsConfirmationStep({ onSubmit }: SmsConfirmationStepProps): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SmsConfirmationFormValues>({
    resolver: zodResolver(smsConfirmationSchema),
    defaultValues: { code: '' },
  });

  async function handleFormSubmit(values: SmsConfirmationFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await onSubmit(values.code);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Código incorrecto');
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Te enviamos un código por SMS. Ingresá los 6 dígitos.
        </Typography>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          label="Código de verificación"
          autoFocus
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
          {...register('code')}
          error={!!errors.code}
          helperText={errors.code?.message}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Verificando...' : 'Verificar'}
        </Button>
      </Stack>
    </form>
  );
}
